<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="550"
    transition="dialog-transition"
  >
    <template #activator="{ on }">
      <v-btn
        v-on="on"
        small
        color="primary"
        outlined
        class="text-none mr-2"
      >
        <v-icon left small>mdi-calendar-plus</v-icon>
        Add Monthly Plan
      </v-btn>
    </template>
    <v-card>
      <v-card-title class="subtitle-1 font-weight-bold">
        Monthly Planning
        <v-spacer />
        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />
      <v-card-text class="pt-4">
        <v-form ref="form" v-model="valid">
          <v-autocomplete
            v-model="form.part"
            :items="partList"
            :rules="[rules.required]"
            item-text="partname"
            item-value="partname"
            label="Part *"
            dense
            outlined
            class="mb-2"
          />
          <v-autocomplete
            v-model="form.color"
            :items="colorList"
            :rules="[rules.required]"
            label="Color *"
            dense
            outlined
            class="mb-2"
          />
          <v-text-field
            v-model="form.quantity"
            :rules="[rules.required, rules.positive]"
            label="Planned qty *"
            type="number"
            dense
            outlined
            class="mb-2"
          />
          <v-text-field
            :value="tonnage"
            label="Production in Tonnage"
            dense
            outlined
            readonly
            class="mb-2"
            :hint="tonnageHint"
            persistent-hint
          />
          <v-menu
            v-model="monthMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template #activator="{ on, attrs }">
              <v-text-field
                :value="formattedMonth"
                :rules="[rules.required]"
                label="Month-Year *"
                readonly
                dense
                outlined
                class="mb-2"
                append-icon="mdi-calendar"
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <v-date-picker
              v-model="form.monthYear"
              type="month"
              @input="monthMenu = false"
            />
          </v-menu>
          <v-autocomplete
            v-model="form.machine"
            :items="machineList"
            item-text="machinename"
            item-value="machinename"
            label="Machine (Optional)"
            dense
            outlined
            clearable
            class="mb-2"
          />
        </v-form>
      </v-card-text>
      <v-divider />
      <v-card-actions class="pa-3">
        <v-spacer />
        <v-btn text class="text-none" @click="closeDialog">
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          class="text-none"
          :disabled="!valid"
          :loading="saving"
          @click="submitPlan"
        >
          <v-icon left small>mdi-check</v-icon>
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapActions } from 'vuex';

export default {
  name: 'MonthlyPlanFormBtn',
  data() {
    return {
      dialog: false,
      valid: false,
      saving: false,
      monthMenu: false,
      tonnage: '0.000',
      editingId: null,
      isLoadingEdit: false,
      form: {
        part: null,
        color: null,
        quantity: null,
        weight: null,
        monthYear: (() => {
          const now = new Date();
          const y = now.getFullYear();
          const m = String(now.getMonth() + 1).padStart(2, '0');
          return `${y}-${m}`;
        })(),
        machine: null,
      },
      rules: {
        required: (v) => !!v || 'This field is required',
        positive: (v) => (v && v > 0) || 'Must be > 0',
      },
    };
  },
  computed: {
    ...mapState('monthlyPlanning', ['parts', 'machines', 'colors', 'weightUnit']),
    // Net weight is entered in grams when the Weight Unit filter is "grams",
    // otherwise in Kg. The divisor to reach tonnes differs accordingly.
    isGrams() {
      return this.weightUnit === 'grams';
    },
    tonnageHint() {
      return this.isGrams
        ? 'Quantity x Net Weight (g) / 1,000,000 = Tonnage'
        : 'Quantity x Net Weight (Kg) / 1000 = Tonnage';
    },
    partList() {
      return this.parts || [];
    },
    machineList() {
      return this.machines || [];
    },
    colorList() {
      if (!this.colors || !this.colors.length) return [];
      const colorNames = this.colors
        .map((c) => c.colorname || c.color || c.name)
        .filter((c) => c != null && c !== '');
      return [...new Set(colorNames)];
    },
    weightList() {
      if (!this.partList.length) return [];
      const weights = this.partList
        .map((p) => p.netweight || p.weight || p.netwt)
        .filter((w) => w != null && w !== '')
        .map((w) => String(w));
      return [...new Set(weights)];
    },
    formattedMonth() {
      if (!this.form.monthYear) return '';
      const [year, month] = this.form.monthYear.split('-');
      const months = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
      ];
      return `${months[parseInt(month, 10) - 1]} ${year}`;
    },
  },
  created() {
    this.$root.$on('edit-monthly-plan', this.loadForEdit);
  },
  beforeDestroy() {
    this.$root.$off('edit-monthly-plan', this.loadForEdit);
  },
  watch: {
    'form.part': function formPartWatcher(partName) {
      if (this.isLoadingEdit) return;
      if (!partName) {
        this.form.weight = null;
        return;
      }
      // Look up weight from part master
      const matched = this.partList.find(
        (p) => p.partname === partName,
      );
      if (matched) {
        const wt = matched.netweight || matched.weight
          || matched.netwt || '';
        this.form.weight = wt ? String(wt) : null;
      } else {
        this.form.weight = null;
      }
    },
    'form.quantity': function formQtyWatcher() {
      this.updateTonnage();
    },
    'form.weight': function formWeightWatcher() {
      this.updateTonnage();
    },
    // Recompute if the user switches the Weight Unit while the form is open.
    weightUnit() {
      this.updateTonnage();
    },
  },
  methods: {
    ...mapActions('monthlyPlanning', ['submitMonthlyPlan']),
    loadForEdit(item) {
      this.isLoadingEdit = true;
      this.editingId = item._id || item.id || (item._ids && item._ids[0]);
      this.form.part = item.partname;
      this.form.color = item.color || null;
      this.form.quantity = item.quantity;
      // Look up weight from parts master by partname only
      let weight = null;
      const matched = this.partList.find(
        (p) => p.partname === item.partname,
      );
      if (matched) {
        weight = matched.netweight || matched.weight || matched.netwt;
      }
      // If still no weight, reverse calc from tonnage and qty
      if (!weight && item.productionintonnage && item.quantity) {
        weight = (item.productionintonnage * 1000) / item.quantity;
      }
      this.form.weight = weight ? String(weight) : null;
      this.form.monthYear = item.monthyear
        ? `${String(item.monthyear).substring(0, 4)}-${String(item.monthyear).substring(4, 6)}`
        : null;
      this.form.machine = item.machinename || null;
      this.updateTonnage();
      this.dialog = true;
      this.$nextTick(() => {
        this.isLoadingEdit = false;
      });
    },
    updateTonnage() {
      const qty = Number(this.form.quantity) || 0;
      const weight = parseFloat(this.form.weight) || 0;
      if (!qty || !weight) {
        this.tonnage = '0.000';
        return;
      }
      // Grams: qty x g / 1,000,000. Kg: qty x kg / 1000.
      const divisor = this.isGrams ? 1000000 : 1000;
      const result = (qty * weight) / divisor;
      this.tonnage = result.toFixed(3);
    },
    async submitPlan() {
      if (!this.$refs.form.validate()) return;
      this.saving = true;
      try {
        const assetId = this.$store.state.helper.selectedAssetId
          || this.$store.state.helper.assetId
          || 0;
        const record = {
          partname: this.form.part,
          color: this.form.color,
          quantity: Number(this.form.quantity),
          netweight: this.form.weight || '',
          productionintonnage: parseFloat(this.tonnage) || 0,
          monthyear: this.form.monthYear
            ? parseInt(`${this.form.monthYear.replace('-', '')}01`, 10)
            : 0,
          machinename: this.form.machine || '',
          assetid: assetId,
        };
        let result;
        if (this.editingId) {
          // Update existing record
          await this.$store.dispatch(
            'element/updateRecordById',
            {
              elementName: 'monthlyplan',
              id: this.editingId,
              payload: record,
            },
            { root: true },
          );
          result = { success: true };
        } else {
          result = await this.submitMonthlyPlan([record]);
        }
        if (result && result.success) {
          this.resetForm();
          this.dialog = false;
          this.$emit('saved');
        } else {
          this.$emit('error', result ? result.error : 'Failed');
        }
      } catch (e) {
        this.$emit('error', e.message || 'Failed to save plan');
      }
      this.saving = false;
    },
    resetForm() {
      this.editingId = null;
      this.isLoadingEdit = false;
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, '0');
      this.form = {
        part: null,
        color: null,
        quantity: null,
        weight: null,
        monthYear: `${y}-${m}`,
        machine: null,
      };
      this.tonnage = '0.000';
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    closeDialog() {
      this.resetForm();
      this.dialog = false;
    },
  },
};
</script>
